import React from 'react'
import { Chart, PointElement } from 'chart.js'
import 'chartjs-chart-graph'
import { EdgeLine, ForceDirectedGraphController } from 'chartjs-chart-graph'
import { Chart as ReactChart } from 'react-chartjs-2'
import colors from '../colors'

Chart.register(ForceDirectedGraphController, PointElement, EdgeLine)

export type Node = { id: string; label: string }
export type Edge = { source: string; target: string; weight: number }

type Props = {
  nodes: Node[]
  edges: Edge[]
}

const GraphChart: React.FC<Props> = ({ nodes, edges }) => {
  const getData = () => {
    const reducedEdges: Edge[] = edges
      .filter((e) => e.target !== e.source)
      .reduce((acc: Edge[], edge) => {
        const edgeIndex = acc.findIndex(
          (n) => (n.source === edge.source && n.target === edge.target) || (n.source === edge.target && n.target === edge.source),
        )
        if (edgeIndex >= 0) {
          const currentEdge = acc[edgeIndex]
          acc[edgeIndex] = {
            ...currentEdge,
            weight: currentEdge.weight + edge.weight,
          }
        } else {
          acc.push(edge)
        }
        return acc
      }, [])

    const getBorderWidths = () => {
      const weights = reducedEdges.map((e) => e.weight)
      const max = Math.max(...weights)
      const maxBorderWidth = 10
      return weights.map((w) => (maxBorderWidth / 100) * ((100 / max) * w))
    }

    const getBorderColors = () => {
      const widths = getBorderWidths()
      return widths.map((w) => {
        if (w > 0) return 'rgb(140,140,140,0.3)'
        return 'transparent'
      })
    }

    return {
      labels: nodes.map((n) => n.id),
      datasets: [
        {
          pointBackgroundColor: colors.map((c) => c.backgroundColor),
          pointRadius: 10,
          pointHoverRadius: 10,
          pointBorderWidth: 0,
          data: nodes,
          edges: reducedEdges,
          borderWidth: getBorderWidths(),
          borderColor: getBorderColors(),
        },
      ],
    }
  }

  const options = {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: () => null,
          label: (context) => {
            return context.raw.label
          },
        },
      },
    },
    responsive: true,
  }

  return <ReactChart type="forceDirectedGraph" redraw data={getData()} options={options} />
}

export default GraphChart
